import $ from 'jquery'
import _ from 'lodash'
import appDisplay from 'scoreboard/components/app-display'
import BootslideMenu from 'scoreboard/components/bootslide/bootslide-menu'
import i18n from 'scoreboard/components/i18n'
import unwrapModel from 'scoreboard/components/unwrap-model'
import BootslideEditMenu from '../bootslide-edit-menu'
import FeatureView from '../feature-view'
import ChartDesignerFeature from '../../model/chart-designer-feature'
import LinesAndBandsDataSource from './lines-and-bands-data-source'
import template from './lines-and-bands.hbs'
import constants from 'scoreboard/components/constants'
import opacityMenu from '../../../components/bootslide/opacity-menu'

/**
 * @typedef ChartLine
 */

/**
 * @typedef ChartFill
 */

/**
 * @typedef LinesAndBands
 * @property {ChartLine[]|ChartFill[]} children
 */

const FEATURE_KEY = 'lines-and-bands'

const DECORATED_PROPERTIES = [
	'className', 'isLine', 'defaultLabel', 'displayLabel', 'error', 'showAddIcon', 'errorMessage']

/**
 * @property {LinesAndBands} this.model the lines and bands model
 */
const LinesAndBands = FeatureView.extend({

	className: FeatureView.prototype.className + ' ' + FEATURE_KEY,

	events: _.extend({
		'click .add-line': 'onClickAddLine',
		'click .add-fill': 'onClickAddFill',
		'click .info, .line, .fill': 'setEditClickTarget'
	}, FeatureView.prototype.events),

	key: _.constant(FEATURE_KEY),

	/**
	 *
	 * @param {LinesAndBands} options.parent
	 */
	initialize: function(options = {}) {
		FeatureView.prototype.initialize.apply(this, arguments)
		this._decorateLinesAndFills(_.map(this.model.children.toArray(), unwrapModel))
		this._listenForChartDesignerSerieRemoval()
	},

	/**
	 * @param {Boolean} [options.changed=false] If the model (e.g. children)
	 *     changed and those changes should be synced/POSTed this flag must be
	 *     set to true.
	 */
	paint: function(options = {}) {
		this._decorateLinesAndFills(this._allLinesAndFills)
		this._syncWithModel(options)
		const context = _.extend({
			label: i18n.get(`charts.feature.${this.key()}`),
			linesAndFills: this._allLinesAndFills,
			lineCount: _.countBy(this._allLinesAndFills, 'type')['line']
		}, this.model.attributes)
		this.$el.empty().append(template(context))

		_.invoke(this, 'secure')

		this.renderErrorState()

		// remove drag handles on the lines if this is anything other than
		// a single trend line
		if (this.model.get('type') !== 'trend-line') {
			this.$('li').removeClass('drag-handle')
		}
	},

	_syncWithModel({changed = false}) {
		// This triggers a POST, so if nothing changed don't touch the model
		if (changed) {
			// #55702 make sure all children get a sortOrder. Easiest thing to do
			// is just set the sortOrder based on the list we've already updated
			this._allLinesAndFills.forEach((f, i) => {
				f.sortOrder = i
			})

			this.model.set('children',
				_.map(this._allLinesAndFills, laf => _.omit(laf, DECORATED_PROPERTIES)))
		}
	},

	_removeLine: function({id, tempId}) {
		// Remove fixed height so the feature can expand
		this.$el.css('height', '')
		const lineIndex = _.findIndex(
			this._allLinesAndFills,
			ChartDesignerFeature.idPredicate(id, tempId)
		)
		// When a line is removed also remove the subsequent fill
		// https://github.com/SpiderStrategies/Scoreboard/issues/36718#issuecomment-859670089
		_.pullAt(this._allLinesAndFills, [lineIndex, lineIndex+1])
		this.paint({changed: true})
		// Recalculate height for transitions now
		this.setHeight()
	},

	/**
	 * @param {Event} e
	 */
	createEditMenu: function(e) {
		const {feature, nextFill, type, menuOptions} = this.createEditMenuContext(e)

		if (type === 'line') {
			menuOptions.deleteFn = () => this._removeLine(feature)
			const menu = new EditLineMenu(menuOptions)
			this._listenForLineChanges(menu, feature, nextFill)
			return menu
		} else if (type === 'fill') {
			return new EditFillMenu(menuOptions)
		} else {
			return new EditMenu(menuOptions)
		}
	},

	createEditMenuContext: function (e) {
		const {feature, nextFill} = this._getClickedFeature(e)
		const {type} = feature
		const featureModel = new ChartDesignerFeature(feature)
		const menuOptions = {
			model: this.chart,
			labFeature: this.model,
			feature: featureModel,
			allowDynamicWidth: true,
			addButton: $(e.target)
		}
		this.listenForFeatureChanges(featureModel, feature)
		return {feature, nextFill, type, menuOptions}
	},

	_listenForChartDesignerSerieRemoval: function() {
		// When a feature is removed we have to check the series and change the
		// scope if there are no more bars and scope is set to "Data Series"
		this.listenTo(this.chart, 'feature:removed', () => {
			const { scope, axis } = unwrapModel(this.model)
			if (scope === 'per-series' && !this.chart.hasBarSeries({axis})) {
				// Use repeating again by if it exists, otherwise repeating
				const breakdownIndex = this.chart.breakdowns.toArray().length
				this.model.set('scope', `per-breakdown${breakdownIndex}`)
			}
		})
	},

	listenForFeatureChanges: function (featureModel, feature) {
		this.listenTo(featureModel, 'change', () => {
			_.extend(feature, unwrapModel(featureModel))
			const {label} = featureModel.attributes
			if (label) {
				delete feature.defaultLabel
			}
			this.paint({changed: true})
		})
	},

	_listenForLineChanges: function (menu, feature, nextFill = {}) {
		this.listenTo(menu, 'duplicate', () => {
			const line = _.cloneDeep(feature)
			ChartDesignerFeature.assignNewIds(line, null, null)
			this.addLine(line)
		})
	},

	/**
	 * Adds the first line, or additional lines
	 * @param {Event} e
	 */
	onClickAddLine: function(e) {
		// Remove fixed height so the feature can expand
		this.$el.css('height', '')
		const {axis} = unwrapModel(this.model)
		const line = {
			tempId: ChartDesignerFeature.tempId(),
			type: 'line',
			axis,
			color: 'gray-medium',
			enabled: true,
			opacity: constants.DEFAULT_LINE_OPACITY
		}
		this.addLine(line)
		this.showDataSourceDialog(this.$(`li[data-temp-id=${line.tempId}]`)) // #40519
		// Recalculate height for transitions now
		this.setHeight()
	},

	addLine: function(line) {
		this.$el.css('height', '')
		this._allLinesAndFills.unshift(line)
		this.paint({changed: true})
	},

	setHeight: function() {
		FeatureView.prototype.setHeight.apply(this, arguments)
		let total = this._allLinesAndFills.length
		let activeContentHeight = this.$('.active-content').height()
		// Content height that never changes so no need to grab it
		let fixedContentHeight = 93
		let hiddenContentHeight = total === 0 ?
			activeContentHeight + constants.CHART_DESIGNER_PANEL_PADDING :
			fixedContentHeight + constants.LINES_AND_BANDS_HEIGHT*total
		this.$el.css('height', `${hiddenContentHeight}px`)
	},

	showDataSourceDialog(target) {
		const {menuOptions} = this.createEditMenuContext({target})
		const {model, labFeature, feature} = menuOptions
		appDisplay.dialog(new LinesAndBandsDataSource({
			model: feature,
			labFeature,
			chart: model
		}))
	},

	/**
	 * Assigns a color to a fill which in effect makes it visible
	 * @param {Event} e
	 */
	onClickAddFill: function(e) {
		let {feature: fill} = this._getClickedFeature(e)
		fill.label = null // will be auto-generated
		this.setEditClickTarget(e)
	},

	setEditClickTarget: function(e) {
		this.attachTooltipTo = $(e.currentTarget)
		this.onEdit(e)
	},

	/**
	 * Decorates the model's linesAndFills so the template can apply the extra
	 * view options (e.g. 'add fill' buttons)
	 *
	 * Rules:
	 * If first child is line, add a 'add fill' item above it
	 * If a line does not have a fill below it, insert 'add fill' below it
	 *
	 * @param {Line|Fill}[] linesAndFills the model property of lines and
	 * fills that are persisted to the database.
	 *
	 * @private
	 */
	_decorateLinesAndFills: function (linesAndFills = []) {

		const allLinesAndFills = this._createLineAndFillArray(linesAndFills)

		// General decoration
		const countByType = { 'fill': 1, 'line': 1 }
		let parentErrorToShow = this.model.get('error')
		_.each(allLinesAndFills, (laf, i) => {
			const {type, label, color, enabled} = laf
			laf.isLine = type === 'line'
			if (laf.isLine) {
				laf.className = color ? 'line' : 'add-line'
				laf.showAddIcon = _.isNil(color)
			} else {
				laf.className = enabled ? `fill	opacity-${laf.opacity}` : 'add-fill'
				laf.showAddIcon = !enabled
			}
			laf.displayLabel = label // displayLabel is ignored on save; don't assign to 'label'
			if (type === 'fill' && !enabled) {
				laf.displayLabel = i18n.get('charts.addFill')
			} else if (_.isNil(laf.displayLabel)) {
				laf.displayLabel = i18n.get(`charts.${type}WithArg`, countByType[type]++)
			}

			if (laf.error) {
				laf.errorMessage = laf.error.message
			} else if (laf.isLine && parentErrorToShow) {
				// When the parent lines/bands feature has an error, we show it
				// in the first child line feature. See designs in #40103
				laf.errorMessage = parentErrorToShow.message
				parentErrorToShow = null
			} else {
				laf.errorMessage = null
			}
		})

		this._allLinesAndFills = allLinesAndFills
	},

	_createLineAndFillArray: function(linesAndFills) {
		let parentIds = {
			parentId: this.model.get('id'),
			tempParentId: this.model.get('tempId')
		}

		const byType = _.groupBy(linesAndFills, 'type')
		const lines = _.reverse(_.sortBy(byType['line'], 'sortValue'))
		if (_.isEmpty(lines)) return []

		const fills = byType['fill']
		let allLinesAndFills = []

		const addFill = (i = 0) => {
			// use existing fill, or add a new one if we don't have enough yet
			const fill = _.size(fills) > i ? fills[i] : newFill(parentIds)
			allLinesAndFills.push(fill)
		}

		addFill() // This is the N+1 Fill

		_.each(lines, (laf, i) => {
			allLinesAndFills.push(laf)
			addFill(i+1)
		})

		return allLinesAndFills
	},

	/**
	 * returns the unwrapped model for the feature the user clicked on.
	 * If the feature was a line, the subsequent fill will also be returned as
	 * `nextFill`
	 *
	 * Any changes to these objects will be synchronized with the model on the
	 * next change event.
	 */
	_getClickedFeature(e) {
		const item = this.$(e.target).closest('li')
		if (item.hasClass('lines-and-bands')) {
			return { feature: this.model.attributes }
		}
		const feature = this._allLinesAndFills[item.index()]
		let nextFill
		if (feature.type === 'line') {
			nextFill = this._allLinesAndFills[item.index() + 1]
		}
		return {feature, nextFill}
	}

})
// End Main View

/**
 * Edit menu for the entire lines and bands feataure.
 * @extends BootslideEditMenu
 */
const EditMenu = BootslideEditMenu.extend(/** @lends EditMenu.prototype */{

	addTopLevelOptions: function(topLevel) {
		topLevel.push(this.createScopeOption())
		topLevel.push(this.createCopyOption(this.labFeature, ChartDesignerFeature))
		topLevel.push(this.createDeleteOption())
	},

	createScopeOption: function() {

		const actionFn = scope => {
			this.labFeature.set('scope', scope)
		}

		const scope = this.labFeature.get('scope')
		const addOption = ({value, icon = value, label, disabledMessage}) => {
			target.push(this.iconActionOption({
				icon,
				value,
				label,
				selected: value === scope,
				disabledMessage,
				actionFn
			}))
		}

		const breakdowns = this.chartDesigner.breakdowns.toArray()
		const breakdownOption = (index, value) => {
			if (breakdowns.length > index) {
				const b = breakdowns[index]
				target.push(this.iconActionOption({
					icon: 'per-breakdown',
					value,
					label: this.chartDesigner.breakdownLabel(b),
					selected: value === scope,
					actionFn
				}))
			}
		}

		const perSeriesOption = () => {
			const hasBars = this.chartDesigner.hasBarSeries({axis: this.feature.get('axis')})
			const disabledMessage = hasBars ? undefined :
				i18n.get('charts.perSeries.disabled',
					this.chartDesigner.breakdownLabel(_.last(breakdowns)))

			addOption({
				value: 'per-series',
				label: 'charts.perSeries',
				disabledMessage
			})
		}

		// Scope options
		const target = []
		addOption({
			value: 'entire-chart',
			label: 'charts.entireChart'
		})
		// https://github.com/SpiderStrategies/Scoreboard/issues/42601#issuecomment-939382986 (reverse order)
		breakdownOption(1, 'per-breakdown2')
		breakdownOption(0, 'per-breakdown1')
		perSeriesOption()

		return {
			label: 'charts.scope',
			layout: 'tiles',
			target
		}
	}
}) // EditMenu

/**
 * Edit menu for a single line.
 * @extends BootslideEditMenu
 */
const EditLineMenu = BootslideEditMenu.extend(/** @lends EditLineMenu.prototype */{

	menuLabel: function() {
		return i18n.get('common.edit2', 'charts.feature.lines-and-bands.line')
	},

	addTopLevelOptions: function(topLevel) {
		topLevel.push(this.createShowOption())
		topLevel.push(this.createEditLabelOption({
			inputLabel: i18n.get('common.labelWithPrefix', 'charts.feature.lines-and-bands.line'),
			doneKey: 'common.done'
		}))
		topLevel.push(createDataSourceOption(this.options))
		this.addColorAndOpacityOptions(topLevel)
		topLevel.push(this.createActionOption({
			label: 'common.duplicate',
			icon: 'copy',
			actionFn: () => this.trigger('duplicate')
		}))
		topLevel.push(this.createDeleteOption(this.options))
	}
}) // EditLineMenu

/**
 * Edit menu for a single fill.
 * @extends BootslideEditMenu
 */
const EditFillMenu = BootslideEditMenu.extend(/** @lends EditFillMenu.prototype */{

	addTopLevelOptions: function(topLevel) {
		topLevel.push(this.createShowOption())
		if (this.feature.get('enabled')) {
			topLevel.push(this.createEditLabelOption({doneKey: 'common.done'}))
			topLevel.push(this.createColorMenu())
			topLevel.push(opacityMenu({ model: this.feature }))
		}
		this.on('show', () => this.render({replace: true}))
	}

}) // EditFillMenu

function createDataSourceOption({model, labFeature, feature}) {
	return BootslideMenu.targetOption('report.designer.setDataSource', () => {
		return new LinesAndBandsDataSource({
			model: feature,
			labFeature,
			chart: model
		})
	})
}

/**
 * Generates a new reference-band fill with all of the default options
 * (disabled, color, opacity)
 */
function newFill({ parentId, tempParentId } = {}) {
	return {
		tempId: ChartDesignerFeature.tempId(),
		parentId,
		tempParentId,
		type: 'fill',
		axis: 'inherit',
		color: 'blue-dark',
		opacity: 17,
		enabled: false,
		label: i18n.get('charts.addFill')
	}
}

_.extend(LinesAndBands, {
	EditFillMenu,
	FEATURE_KEY,
	DECORATED_PROPERTIES,
	newFill
})

export default LinesAndBands

LinesAndBands.__filename__ = 'charts/edit/analytics/lines-and-bands.js'
